import React from "react";
import {
  Button,
  Container,
  TextField,
  Typography,
  InputAdornment,
  Avatar,
  Paper,
  Grid,
  Divider,
  IconButton,
} from "@mui/material";
import Logo from "../assets/Logo.png";
import { useParams, useNavigate } from "react-router-dom";
import { resetPassword } from "../actions/auth";
import { useDispatch, useSelector } from "react-redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import "./UserAccountPage";

export default function ResetPassword() {
  const [error, setError] = React.useState("");
  const [loading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(false);

  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const { token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const message = useSelector((state) => state);

  const [postData, setPostData] = React.useState({
    password: "",
    passwordConfirm: "",
  });

  function sendData() {
    if (!postData.password || !postData.passwordConfirm) {
      setError("Please fill in required fields.");
      return;
    }

    if (postData.password !== postData.passwordConfirm) {
      setError("Passwords do not match.");
      return;
    }

    const passwordPattern =
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^\w\s]).{8,}$/;
    if (!passwordPattern.test(postData.password)) {
      setError(
        "Password must include uppercase, lowercase, number, symbol, and be at least 8 characters long.",
      );
      return;
    }

    setError("");
    setErrorMessage(true);
    setIsLoading(true);

    dispatch(resetPassword(token, postData));

    setTimeout(() => {
      setIsLoading(false);
      setPostData({
        password: "",
        passwordConfirm: "",
      });
    }, 3000);
  }

  return (
    <div className="auth-wrapper">
      <Container component="main" maxWidth="xs" className="auth-container">
        {/* Alerts */}
        {errorMessage && (
          <Stack sx={{ width: "100%", marginBottom: "1rem" }} spacing={1}>
            {message.auth.resetError && (
              <Alert severity="error" className="auth-alert">
                Error: {message.auth.resetError}
              </Alert>
            )}
            {message.auth.resetSuccess && (
              <Alert severity="success" className="auth-alert">
                {message.auth.resetSuccess}
              </Alert>
            )}
          </Stack>
        )}

        <Paper elevation={6} className="auth-card">
          {/* Logo + brand */}
          <div className="auth-logo-wrap">
            <img src={Logo} alt="SRB logo" className="auth-logo" />
            <div className="auth-logo-text">
              <span className="auth-bank-name">
                NYANKUMASI-AHENKURO RURAL BANK PLC
              </span>
              <span className="auth-suite-name">CBX Suite</span>
            </div>
          </div>

          {/* Lock + title */}
          <div className="auth-header-row">
            <Avatar className="auth-avatar">
              <LockOutlinedIcon />
            </Avatar>
            <Typography className="auth-title">Create New Password</Typography>
          </div>

          {/* Subtitle */}
          <Typography className="auth-subtitle">
            Set a new password for your account. Make sure it is strong and not
            used anywhere else.
          </Typography>

          <Divider className="auth-divider" />

          {/* Form */}
          <div className="auth-form">
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography className="auth-label">New Password *</Typography>
                <TextField
                  required
                  fullWidth
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={postData.password}
                  onChange={(e) =>
                    setPostData({ ...postData, password: e.target.value })
                  }
                  size="small"
                  variant="outlined"
                  className="auth-password-input"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowNewPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showNewPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className="auth-label">
                  Confirm New Password *
                </Typography>
                <TextField
                  required
                  fullWidth
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={postData.passwordConfirm}
                  onChange={(e) =>
                    setPostData({
                      ...postData,
                      passwordConfirm: e.target.value,
                    })
                  }
                  size="small"
                  variant="outlined"
                  className="auth-password-input"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {error && <span className="auth-error-text">{error}</span>}
          </div>

          <Divider className="auth-divider" />

          {/* Actions */}
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            className="auth-actions"
          >
            <Grid item>
              <Button
                variant="text"
                className="auth-btn auth-btn-secondary"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={sendData}
                variant="contained"
                className="auth-btn auth-btn-primary"
                disabled={loading}
              >
                {loading ? "Changing..." : "Update Password"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </div>
  );
}
